<template>
  <div class="account-modal-overlay" @click.self="request_close">
    <div class="account-modal" role="dialog" aria-modal="true" aria-labelledby="account-modal-title">
      <div class="account-modal-titlebar">
        <h3 id="account-modal-title" class="account-modal-title">Trocar conta</h3>
        <button type="button" class="account-modal-close" aria-label="Fechar modal" :disabled="busy" @click="request_close">
          <span class="material-icons">close</span>
        </button>
      </div>

      <div class="account-modal-body">
        <p class="account-modal-description">
          {{
            is_master
              ? 'Escolha uma conta sua ou busque qualquer cliente ou fornecedor ativo.'
              : 'Escolha a conta que deseja acessar agora.'
          }}
        </p>

        <label class="account-modal-search">
          <span class="material-icons account-modal-search-icon">search</span>
          <input
            ref="search"
            :value="query"
            class="account-modal-search-input"
            type="search"
            :placeholder="is_master ? 'Buscar empresa, CNPJ ou código' : 'Buscar empresa'"
            @input="on_input"
          />
        </label>

        <!-- So a lista rola: titulo, descricao e busca ficam fixos em cima. -->
        <div class="account-modal-scroll">
          <ul v-if="local_rows.length" class="account-modal-list">
            <li v-for="row in local_rows" :key="row.key">
              <button
                type="button"
                class="account-modal-row"
                :class="{ 'account-modal-row--selected': row.key === selected_key && !row.current }"
                :disabled="busy || row.current"
                :aria-pressed="row.key === selected_key && !row.current ? 'true' : 'false'"
                @click="selected_key = row.key"
              >
                <span class="account-modal-avatar" :style="{ background: row.color }" aria-hidden="true">
                  {{ row.initials }}
                </span>
                <span class="account-modal-labels">
                  <span class="account-modal-name">{{ row.primary }}</span>
                  <span class="account-modal-meta">{{ row.secondary }}</span>
                </span>
                <span class="account-modal-radio" aria-hidden="true"><span class="account-modal-radio-dot"></span></span>
              </button>
            </li>
          </ul>
          <!-- Com o grupo master aberto, a linha de status dele ja diz o que ha. -->
          <p v-else-if="!show_master_group" class="account-modal-hint account-modal-hint--center">
            Nenhuma empresa encontrada.
          </p>

          <div v-if="show_master_group" role="group" aria-label="Acesso master" class="account-modal-group">
            <p class="account-modal-group-title">Acesso master</p>
            <p v-if="master_status === 'loading'" class="account-modal-hint">Buscando…</p>
            <p v-else-if="master_status === 'error'" class="account-modal-hint">Não foi possível buscar.</p>
            <p v-else-if="remote_rows.length === 0" class="account-modal-hint">
              Nenhum cliente ou fornecedor ativo encontrado.
            </p>
            <ul v-else class="account-modal-list">
              <li v-for="row in remote_rows" :key="row.key">
                <button
                  type="button"
                  class="account-modal-row"
                  :class="{ 'account-modal-row--selected': row.key === selected_key && !row.current }"
                  :disabled="busy || row.current"
                  :aria-pressed="row.key === selected_key && !row.current ? 'true' : 'false'"
                  @click="selected_key = row.key"
                >
                  <span class="account-modal-avatar" :style="{ background: row.color }" aria-hidden="true">
                    {{ row.initials }}
                  </span>
                  <span class="account-modal-labels">
                    <span class="account-modal-name">{{ row.primary }}</span>
                    <span class="account-modal-meta">{{ row.secondary }}</span>
                  </span>
                  <span class="account-modal-radio" aria-hidden="true"><span class="account-modal-radio-dot"></span></span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="account-modal-footer">
        <button
          type="button"
          class="account-modal-submit"
          :disabled="!selected || busy"
          @click="submit"
        >
          <span v-if="busy" class="account-modal-spinner" aria-hidden="true"></span>
          {{ selected ? 'Continuar' : 'Selecione uma empresa' }}
        </button>
        <p v-if="error" role="alert" class="account-modal-error">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  MIN_SEARCH_LENGTH,
  accountKey,
  avatarColor,
  companyNameOf,
  filterOwnAccounts,
  initialsOf,
  secondaryLabel,
} from "@/services/masterAccess";

/**
 * So apresentacao: busca, troca de conta e redirecionamento ficam no Header,
 * que alimenta este modal. Escolher e confirmar em dois passos, porque entrar
 * numa conta tira o usuario da sessao atual.
 */
export default {
  props: {
    accounts: { type: Array, default: () => [] },
    current_account_id: { type: Number, default: null },
    is_master: { type: Boolean, default: false },
    // Resultados da busca master ja sem as contas que aparecem na lista de cima.
    master_results: { type: Array, default: () => [] },
    master_status: { type: String, default: "idle" },
    busy: { type: Boolean, default: false },
    error: { type: String, default: null },
  },
  data() {
    return {
      query: "",
      selected_key: null,
    };
  },
  computed: {
    term() {
      return this.query.trim();
    },
    show_master_group() {
      return this.is_master && this.term.length >= MIN_SEARCH_LENGTH;
    },
    local_rows() {
      return filterOwnAccounts(this.accounts, this.term).map((account, index) => this.to_row(account, index));
    },
    remote_rows() {
      if (!this.show_master_group || this.master_status !== "idle") {
        return [];
      }
      const offset = this.accounts.length;
      return this.master_results.map((account, index) => this.to_row(account, index + offset));
    },
    selected() {
      // Sai das linhas visiveis de proposito: uma escolha que o termo atual
      // escondeu nao pode continuar valendo para o "Continuar".
      const key = this.selected_key;
      const row = this.local_rows.concat(this.remote_rows).find((r) => r.key === key && !r.current);
      return row || null;
    },
  },
  methods: {
    to_row(account, index) {
      const current = account.id !== null && account.id === this.current_account_id;
      const primary = companyNameOf(account) || account.name;
      return {
        key: accountKey(account),
        account,
        current,
        primary,
        secondary: secondaryLabel(account, current),
        initials: initialsOf(primary),
        color: avatarColor(index),
      };
    },
    on_input(event) {
      this.query = event.target.value;
      this.$emit("search", this.query);
    },
    submit() {
      if (this.selected && !this.busy) {
        this.$emit("select", this.selected.account);
      }
    },
    request_close() {
      // Troca em andamento: fechar agora perderia o erro, se ela falhar.
      if (!this.busy) {
        this.$emit("close");
      }
    },
    on_keydown(event) {
      if (event.key === "Escape") {
        this.request_close();
      }
    },
  },
  mounted() {
    document.addEventListener("keydown", this.on_keydown);
    this.$nextTick(() => {
      if (this.$refs.search) this.$refs.search.focus();
    });
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.on_keydown);
  },
};
</script>

<style lang="scss" scoped>
@import "@/sass/commons/_variables";

.account-modal-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: rgba(0, 0, 0, 0.45);
  font-family: $font-default;
}
.account-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 48px);
  overflow: hidden;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  color: #1f2937;
}
.account-modal-titlebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
}
.account-modal-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
}
.account-modal-close {
  display: flex;
  padding: 4px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  .material-icons {
    font-size: 28px;
  }
  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}
/* O corpo nao rola: titulo, descricao e busca ficam em cima, o rodape embaixo,
   e so .account-modal-scroll (lista propria + grupo master) rola. */
.account-modal-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  padding: 8px 24px 0;
}
.account-modal-description,
.account-modal-search {
  flex: none;
}
/* O padding de 2px (desfeito na margem) evita cortar o anel da linha escolhida. */
.account-modal-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  margin: 0 -2px;
  padding: 2px;
}
.account-modal-description {
  margin: 0 0 20px;
  font-size: 14px;
  color: #6b7280;
}
.account-modal-search {
  position: relative;
  display: block;
  margin: 0 0 16px;
}
.account-modal-search-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  font-size: 18px;
  color: #6b7280;
  pointer-events: none;
}
.account-modal-search-input {
  width: 100%;
  height: 44px;
  padding: 0 12px 0 38px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  color: #1f2937;
  outline: none;
  &:focus {
    border-color: var(--primary-color, #ff5722);
  }
}
.account-modal-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 0 12px;
  padding: 0;
  list-style: none;
}
.account-modal-row {
  position: relative;
  z-index: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}
.account-modal-row--selected {
  border-color: var(--primary-color, #ff5722);
  box-shadow: 0 0 0 2px var(--primary-color-opacity, rgba(255, 87, 34, 0.15));
  /* Tinta leve da cor primaria sem color-mix: a cor vem do whitelabel em runtime. */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: inherit;
    background: var(--primary-color, #ff5722);
    opacity: 0.06;
    z-index: -1;
    pointer-events: none;
  }
  .account-modal-radio {
    border-color: var(--primary-color, #ff5722);
    background: var(--primary-color, #ff5722);
  }
  .account-modal-radio-dot {
    opacity: 1;
  }
}
.account-modal-avatar {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
}
.account-modal-labels {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}
.account-modal-name,
.account-modal-meta {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.account-modal-name {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
}
.account-modal-meta {
  font-size: 13px;
  color: #6b7280;
}
.account-modal-radio {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-radius: 50%;
  transition: all 0.15s ease;
}
.account-modal-radio-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #ffffff;
  opacity: 0;
}
.account-modal-group {
  margin-bottom: 20px;
}
.account-modal-group-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
}
.account-modal-hint {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}
.account-modal-hint--center {
  padding: 8px 0;
  text-align: center;
}
.account-modal-footer {
  flex: none;
  padding: 16px 24px 24px;
  border-top: 1px solid #f3f4f6;
}
.account-modal-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 8px;
  background: var(--primary-color, #ff5722);
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}
.account-modal-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: account-modal-spin 0.8s linear infinite;
}
@keyframes account-modal-spin {
  to {
    transform: rotate(360deg);
  }
}
.account-modal-error {
  margin: 12px 0 0;
  font-size: 14px;
  color: #d32f2f;
}
/* Tela baixa (celular deitado): cabecalho, busca e rodape fixos nao cabem
   junto com a lista, e o overflow do card cortaria o "Continuar". Ai o card
   inteiro rola, em vez de so a lista. */
@media (max-height: 520px) {
  .account-modal-overlay {
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .account-modal {
    max-height: calc(100vh - 16px);
    overflow-y: auto;
  }
  .account-modal-body,
  .account-modal-scroll {
    flex: none;
    min-height: auto;
    overflow: visible;
  }
}
</style>
